/**
 * View Selector
 *
 * Switches between supported Space views while keeping legacy view aliases
 * normalized to the current product vocabulary.
 *
 * Primary responsibilities:
 * - Render the active Space view tabs
 * - Hide unsupported legacy aliases from the UI
 *
 * Dependencies:
 * - shadcn tabs and dropdown primitives
 */

"use client"

import { FC, useState, type ReactNode } from "react"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Button } from "@/components/ui/button"
import {
  IconList,
  IconLayoutKanban,
  IconTimeline,
  IconCalendar,
  IconTable,
  IconChartBar,
  IconPlus,
  IconX,
  IconSettings,
  IconChevronDown
} from "@tabler/icons-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

export type ViewType =
  | "list"
  | "board"
  | "kanban"
  | "timeline"
  | "calendar"
  | "table"
  | "gantt"
  | "chart"
  | string // Allow custom views

interface ViewSelectorProps {
  currentView: ViewType
  onViewChange: (view: ViewType) => void
  availableViews?: ViewType[]
  onAddView?: () => void
  onConfigureView?: () => void
  configureEnabledViews?: ViewType[]
  viewLabels?: Partial<Record<string, string>>
  viewIcons?: Partial<Record<string, ReactNode>>
  className?: string
}

const DEFAULT_VIEWS: ViewType[] = ["list", "board", "timeline", "calendar"]

const normalizeView = (view: ViewType): ViewType => {
  return view === "kanban" ? "board" : view
}

const VIEW_ICONS: Record<string, React.ReactNode> = {
  list: <IconList size={16} />,
  board: <IconLayoutKanban size={16} />,
  timeline: <IconTimeline size={16} />,
  calendar: <IconCalendar size={16} />,
  table: <IconTable size={16} />,
  gantt: <IconChartBar size={16} />
}

const VIEW_LABELS: Record<string, string> = {
  list: "List",
  board: "Board",
  timeline: "Timeline",
  calendar: "Calendar",
  table: "Table",
  gantt: "Gantt"
}

export const ViewSelector: FC<ViewSelectorProps> = ({
  currentView,
  onViewChange,
  availableViews = DEFAULT_VIEWS,
  onAddView,
  onConfigureView,
  configureEnabledViews = ["board", "list"],
  viewLabels,
  viewIcons,
  className
}) => {
  const [customViews, setCustomViews] = useState<ViewType[]>([])

  const normalizedCurrentView = normalizeView(currentView)

  const allViews = Array.from(
    new Set([
      ...availableViews.map(normalizeView),
      ...(availableViews.map(normalizeView).includes(normalizedCurrentView)
        ? []
        : [normalizedCurrentView]),
      ...customViews.map(normalizeView)
    ])
  )

  const getViewLabel = (view: ViewType) => {
    return viewLabels?.[view] || VIEW_LABELS[view] || view
  }

  const getViewIcon = (view: ViewType) => {
    return viewIcons?.[view] || VIEW_ICONS[view] || <IconList size={16} />
  }

  const handleRemoveView = (view: ViewType) => {
    const normalizedView = normalizeView(view)

    if (DEFAULT_VIEWS.includes(normalizedView)) return

    setCustomViews(customViews.filter(v => normalizeView(v) !== normalizedView))

    if (normalizedCurrentView === normalizedView) {
      onViewChange("list") // Switch to list view when removing current view
    }
  }

  const canConfigureCurrentView =
    Boolean(onConfigureView) &&
    configureEnabledViews
      .map(normalizeView)
      .includes(normalizedCurrentView)

  const addableViews = Object.entries(VIEW_LABELS).filter(([key]) => {
    return !allViews.includes(normalizeView(key))
  })

  const hasDisplayMenu = Boolean(onConfigureView || onAddView)

  return (
    <div className={cn("flex min-w-0 flex-wrap items-center gap-2", className)}>
      <Tabs
        value={normalizedCurrentView}
        onValueChange={onViewChange}
        className="min-w-0 max-w-full overflow-x-auto"
      >
        <TabsList className="bg-muted/30 h-9 w-max">
          {allViews.map((view) => (
            <TabsTrigger
              key={view}
              value={view}
              className="relative shrink-0 gap-2 text-xs"
            >
              {getViewIcon(view)}
              <span className="hidden sm:inline">
                {getViewLabel(view)}
              </span>
              {!DEFAULT_VIEWS.includes(view) && customViews.includes(view) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveView(view)
                  }}
                  className="hover:bg-destructive/20 ml-1 rounded-md p-0.5 transition-all duration-150 ease-out"
                >
                  <IconX size={12} />
                </button>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {hasDisplayMenu && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-9 shrink-0 gap-2 sm:min-w-[108px] sm:justify-between"
            >
              <span className="flex items-center gap-2">
                <IconSettings size={16} />
                <span className="hidden sm:inline">Display</span>
              </span>
              <IconChevronDown size={14} className="text-muted-foreground hidden sm:block" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {canConfigureCurrentView ? (
              <DropdownMenuItem onClick={onConfigureView}>
                <IconSettings size={16} className="mr-2" />
                Configure {getViewLabel(normalizedCurrentView)}
              </DropdownMenuItem>
            ) : null}

            {canConfigureCurrentView && addableViews.length > 0 ? (
              <DropdownMenuSeparator />
            ) : null}

            {onAddView && addableViews.map(([key, label]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => {
                    const nextView = normalizeView(key)

                    setCustomViews([
                      ...customViews.filter(view => normalizeView(view) !== nextView),
                      nextView
                    ])
                    onViewChange(nextView)
                  }}
                >
                  <span className="mr-2">{getViewIcon(normalizeView(key))}</span>
                  Add {viewLabels?.[normalizeView(key)] || label}
                </DropdownMenuItem>
              ))}

            {onAddView && addableViews.length === 0 ? (
              <DropdownMenuItem disabled>
                All views are already added
              </DropdownMenuItem>
            ) : null}

            {!canConfigureCurrentView && !onAddView ? (
              <DropdownMenuItem disabled>
                No display options
              </DropdownMenuItem>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
